import {
    Box,
    Button,
    Code,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Stack,
    Switch,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useEntitySearch } from '../../../components/command-palette/useEntitySearch';
// Imported from the module rather than the `states` barrel on purpose: the
// barrel also re-exports `SkeletonCardGrid`, which pulls in `components/card`
// and therefore Chakra's `forwardRef` — and every test that mocks
// `@chakra-ui/react` as plain DOM (this modal's own suite does) then fails to
// even LOAD the file, with an error naming Card.tsx rather than anything here.
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { CourtDto, SportType } from '../../../models/court/dto/court.dto';
import { GameRankedMode, GameType } from '../../../models/game/dto/game.dto';
import {
    AdminBookingPaymentMode,
    AdminBookingResponseDto,
    CreateAdminBookingDto,
} from '../../../models/payment/dto/admin-booking.dto';
import { AdminGuestPlayerResponseDto } from '../../../models/user/dto/admin-guest-player.dto';
import { buildAdminBookingAmountLabel } from '../utils/admin-booking-amount-label';
import {
    buildPaymentLinkShareMessage,
    buildSmsShareUrl,
    buildWhatsAppShareUrl,
    splitSearchIntoName,
} from '../utils/guest-player';
import GuestPlayerForm from './GuestPlayerForm';
import { isUnpriceableBookingError } from '../../../utils/unpriceableBooking';
import { useAdminBookingPricePreview } from '../hooks/useAdminBookingPricePreview';
import { useCreateAdminBooking } from '../hooks/useCreateAdminBooking';
import { useGetOrganizationGameCapacityOptions } from '../../organization/hooks/useOrganizationGameCapacityOptions';
import { useGetOrganizationFeatureFlags } from '../../organization/hooks/useGetOrganizationFeatureFlags';
import { isRecordingAllowed } from '../../organization/utils/recording-gate';
import {
    isBookableCapacity,
    resolveAdminBookingCapacity,
    resolveOfferableAdminBookingCapacities,
    sportTypeToCapacity,
} from '../../../utils/sport-capacity';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    organizationId: string;
    courts: CourtDto[];
    // Optional prefill if invoked from a calendar slot click
    initialCourtId?: number;
    initialStartTime?: Date;
    initialDurationMinutes?: number;
    onCreated?: () => void;
};

// Render a deadline value (minutes) as a friendly French label and tag
// the org's default with "(par défaut)" so the operator can see the
// baseline at a glance. 60 → "1 h", 1440 → "24 h", 90 → "1 h 30".
// Stripe Checkout's own bounds on `expires_at`, mirroring the API's
// `payment/utils/payment-deadline.ts`: it refuses a session expiring sooner
// than 30 minutes out, and 1430 keeps a 10-minute buffer inside its 24 h
// ceiling for clock drift. The API HARD-rejects anything outside this range on
// both the per-booking override and the organization default, so an option
// outside it is a guaranteed 400 on submit. The "24 h" preset used to be 1440,
// which the API's own DTO already declared out of range.
const MIN_DEADLINE_MINUTES = 30;
const MAX_DEADLINE_MINUTES = 1430;
const DEADLINE_PRESETS = [30, 60, 120, 360, MAX_DEADLINE_MINUTES];

// An organization whose stored default predates the API's write-time check may
// still hold an out-of-range value (the settings card offered 5-minute windows
// until it was corrected). Clamp before seeding the picker, exactly as the API
// clamps it when it resolves the window — otherwise the modal opens preselected
// on a value the server will refuse.
function clampDeadlineMinutes(minutes: number): number {
    if (!Number.isFinite(minutes)) return MIN_DEADLINE_MINUTES;
    return Math.min(
        Math.max(Math.round(minutes), MIN_DEADLINE_MINUTES),
        MAX_DEADLINE_MINUTES,
    );
}

function formatDeadlineLabel(minutes: number, isDefault: boolean): string {
    let label: string;
    if (minutes < 60) {
        label = `${minutes} min`;
    } else if (minutes % 60 === 0) {
        label = `${minutes / 60} h`;
    } else {
        const hours = Math.floor(minutes / 60);
        const rem = minutes % 60;
        label = `${hours} h ${rem}`;
    }
    return isDefault ? `${label} (par défaut)` : label;
}

function roundUpToNextQuarterHour(date: Date): Date {
    // Snap an arbitrary timestamp up to the next :00, :15, :30, or :45.
    // The DatePicker's timeIntervals=15 only constrains the dropdown — if
    // we hand it an off-step `selected`, the input renders that value
    // until the operator picks a fresh one. Snap so the default sits on
    // a valid step from frame 1.
    const out = new Date(date);
    const minutesUntilNextStep =
        (15 - (out.getMinutes() % 15)) % 15 || (out.getSeconds() > 0 ? 15 : 0);
    if (minutesUntilNextStep > 0) {
        out.setMinutes(out.getMinutes() + minutesUntilNextStep);
    }
    out.setSeconds(0, 0);
    return out;
}

export default function CreateAdminBookingModal({
    isOpen,
    onClose,
    organizationId,
    courts,
    initialCourtId,
    initialStartTime,
    initialDurationMinutes = 90,
    onCreated,
}: Props) {
    const toast = useToast();

    const [courtId, setCourtId] = useState<number | undefined>(
        initialCourtId ?? courts[0]?.id,
    );
    const [startTime, setStartTime] = useState<Date | null>(() =>
        roundUpToNextQuarterHour(
            initialStartTime ?? new Date(Date.now() + 60 * 60 * 1000),
        ),
    );
    const [durationMinutes, setDurationMinutes] = useState<number>(
        initialDurationMinutes,
    );
    const [paymentMode, setPaymentMode] = useState<AdminBookingPaymentMode>(
        AdminBookingPaymentMode.STRIPE_LINK,
    );
    const [sport, setSport] = useState<SportType>(
        courts.find((c) => c.id === (initialCourtId ?? courts[0]?.id))?.sports?.[0] ??
            SportType.PADEL,
    );
    // PUBLIC = open join, others can pay their share through the regular
    // join flow. PRIVATE = only the organizer's group plays. Default PUBLIC
    // because that's the more common operator intent and matches the
    // pre-selector behavior.
    const [gameType, setGameType] = useState<GameType>(GameType.PUBLIC);
    // The operator's explicit pick, or null to follow whatever the
    // organization offers for the selected sport. It stays null until they
    // touch the control so switching sport re-resolves instead of carrying a
    // padel-shaped 4 onto a tennis court.
    const [pickedCapacity, setPickedCapacity] = useState<number | null>(null);
    // Per-game recording opt-in. Only offered when the SELECTED COURT has a
    // camera (see the showRecordingToggle gate below).
    const [recordingEnabled, setRecordingEnabled] = useState<boolean>(false);

    // Per-booking override for the Stripe Checkout expires_at window. The
    // initial value comes from the org's bookingPaymentDeadlineMinutes
    // (re-set via the useEffect below once the org query resolves), and
    // the operator can pick a different value from the preset chooser.
    // Sent verbatim to the API; the server clamps to [30, 1430] which
    // matches Stripe's hard expires_at floor + ceiling.
    const [paymentDeadlineMinutes, setPaymentDeadlineMinutes] =
        useState<number>(30);
    // Default the payment deadline from the org being booked (the picker's
    // selected org), NOT the admin's home org — a superadmin can book into a
    // different org via the picker, and selectedOrganization stays in sync with
    // the selected location (and thus the organizationId prop). Falls back to
    // 30 until the org list resolves.
    const { selectedOrganization } = useSelectedOrganization();
    const orgDefaultDeadline = clampDeadlineMinutes(
        selectedOrganization?.bookingPaymentDeadlineMinutes ?? 30,
    );
    // Only offer the recording toggle when the SELECTED COURT actually has a
    // camera. `recordingAvailable` is the org-wide coarse flag — it flips true
    // as soon as the org has ONE provisioned VenueAgent — so gating on it alone
    // put the toggle on every court of a partially equipped venue, and flipping
    // it there produced a booking that silently never records.
    //
    // Keyed by the `organizationId` PROP rather than the picker's selected org:
    // the prop is the org being booked, and a superadmin can book into a
    // different org than the one the picker tracks. Checking a court id from
    // the booked org against another tenant's capable set is exactly the
    // mismatch this gate exists to prevent.
    const { data: recordingFeatureFlags } =
        useGetOrganizationFeatureFlags(organizationId);
    // Until the flags land — and if the request fails, since react-query leaves
    // `data` undefined — fall back to precisely today's org-level behaviour
    // instead of hiding a toggle the operator legitimately has. This gate can
    // then only ever NARROW what is offered, never widen it, and the API stays
    // authoritative on whether a booking actually records (a game flagged for
    // recording on a court no agent covers gets an auditable placeholder
    // recording, not a recording — `agent-recording.service.ts`).
    //
    // The fallback is fenced to the org being BOOKED for the same reason the
    // query above is keyed by the prop: `selectedOrganization` is the PICKER's
    // org, and a coarse "this org has cameras" answer for one tenant says
    // nothing about another. Today the two cannot actually diverge while this
    // modal is mounted — every org switch runs through OrganizationPicker,
    // which nulls the selected location, and the parent unmounts us the moment
    // `selectedLocation.organizationId` is gone — but that invariant lives in
    // three other files and nothing here would notice it breaking. Refusing to
    // read a foreign tenant's flag costs one comparison and, in the only state
    // it changes, hides a toggle for the sub-second until the real flags land.
    const coarseOrgRecording =
        selectedOrganization && selectedOrganization.id === organizationId
            ? (selectedOrganization.recordingAvailable ?? false)
            : false;
    const showRecordingToggle = recordingFeatureFlags
        ? isRecordingAllowed(recordingFeatureFlags, courtId)
        : coarseOrgRecording;

    // Drop a stale opt-in the moment the toggle stops being offered: the
    // operator switches from a camera-equipped court to one without, or the
    // flags land late and disqualify the court already selected. Without this
    // the hidden state keeps its last value and the submit body below still
    // ships `recordingEnabled: true` for a court that cannot record.
    useEffect(() => {
        if (!showRecordingToggle && recordingEnabled) {
            setRecordingEnabled(false);
        }
    }, [showRecordingToggle, recordingEnabled]);

    // Keep the selected court inside the list this modal was actually given.
    //
    // `courtId` is seeded once from `initialCourtId ?? courts[0]?.id` and after
    // that is only ever written by the court <Select> or by the open-edge
    // branch below — which runs solely when a calendar drag supplied an
    // `initialCourtId`, and so not at all for the standalone "new booking"
    // button. Meanwhile the parent keeps this component mounted across
    // LOCATION switches as well as across opens: `useGetCourts` holds every
    // visited location forever (`cacheTime: Infinity`) and seeds its empty
    // placeholder only for a location it has never fetched, so returning to a
    // location already seen swaps `courts` in place with no empty frame for
    // the parent's `courts.length > 0` guard to unmount on.
    //
    // Left alone the modal therefore keeps a court from the PREVIOUS location:
    // the recording gate above tests a court the operator cannot see selected,
    // and the submit body books it — a court at a venue they have left, which
    // is a wrong booking whether or not anything else notices.
    //
    // The reachable case is a location switch WITHIN one organization, which
    // is the only kind the location picker offers: an organization switch goes
    // through OrganizationPicker, which nulls the selected location first, and
    // the parent then unmounts this modal (its guard needs
    // `selectedLocation.organizationId`) so a fresh one is built. That is the
    // upstream reason a foreign-tenant court cannot reach the payload — not
    // anything in this file — so this effect is also what keeps that true if
    // someone ever adds an org switch that leaves the location standing.
    useEffect(() => {
        if (courts.length === 0) return;
        if (courtId != null && courts.some((c) => c.id === courtId)) return;
        const fallback = courts[0];
        setCourtId(fallback.id);
        // Re-derive the sport exactly as the <Select> and the open-edge effect
        // do — a padel-only replacement court must not inherit the vanished
        // court's tennis, which travels on the booking and keys the capacity
        // options.
        if (fallback.sports?.[0]) setSport(fallback.sports[0]);
    }, [courts, courtId]);

    useEffect(() => {
        // Pull the org default in once it lands. The picker stays editable
        // afterwards so the operator can still override per booking.
        setPaymentDeadlineMinutes(orgDefaultDeadline);
    }, [orgDefaultDeadline]);

    // Re-sync the drag-prefill fields every time the modal transitions
    // open. The parent keeps this component mounted to preserve the close
    // animation, so useState only runs once and a SECOND drag (different
    // duration / start time / court) would still show the FIRST drag's
    // values. Depend only on isOpen so a parent re-render mid-edit doesn't
    // wipe what the operator typed; we read the latest prefill from the
    // closure at open-edge time.
    useEffect(() => {
        if (!isOpen) return;
        setStartTime(
            roundUpToNextQuarterHour(
                initialStartTime ?? new Date(Date.now() + 60 * 60 * 1000),
            ),
        );
        setDurationMinutes(initialDurationMinutes);
        // Drop the previous booking's capacity pick — the modal stays mounted
        // between opens, and a stale pick would silently seat the next game.
        setPickedCapacity(null);
        // Same reasoning for the recording opt-in: it is a per-booking choice,
        // and the mounted-between-opens modal would otherwise silently record
        // the NEXT game because the operator ticked the box on the last one.
        setRecordingEnabled(false);
        if (initialCourtId != null) {
            setCourtId(initialCourtId);
            const court = courts.find((c) => c.id === initialCourtId);
            if (court?.sports?.[0]) setSport(court.sports[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Which capacities this organization sells for the selected sport. Same
    // rows the player-facing picker reads, so the operator books at a capacity
    // that actually has a price — and, critically, one the money paths accept:
    // `resolveBookingCapacity` rejects any capacity that is neither configured
    // for the sport nor the sport's legacy default, and it rejects anything
    // outside [1, 20] outright. The hook scopes the query to this org.
    //
    // `isError` is taken because a REFUSED read is not an organization that
    // sells one capacity (R95-2). `GET organization-sport-game-capacity-options`
    // is behind `AdminAuthGuard` plus `hasOrganizationAdminAccess`
    // (`api/src/organization/controller/organization-sport-game-capacity-option.controller.ts`,
    // read this run), so a stale dashboard token, an operator who is a MEMBER
    // rather than an admin of the selected org, or any 5xx leaves
    // `capacityOptions === undefined`. `offeredCapacities` is then empty, which
    // sends this modal down the UNCONFIGURED branch below — and that branch does
    // not merely show a short list, it tells the operator in writing to go and
    // configure capacities they have already configured, while seating and
    // pricing the booking at the sport's legacy capacity instead of the one the
    // customer asked for.
    const { data: capacityOptions, isError: isCapacityOptionsError } =
        useGetOrganizationGameCapacityOptions(organizationId, { sport });

    const offeredCapacities = useMemo(
        () =>
            (capacityOptions ?? [])
                // The server drops unbookable options from the player-facing
                // offering too; showing one here would send the operator
                // straight to a 400 at submit.
                .filter((option) => isBookableCapacity(option.capacity))
                .map((option) => ({
                    capacity: option.capacity,
                    isDefault: option.isDefault,
                }))
                .sort((a, b) => a.capacity - b.capacity),
        [capacityOptions],
    );

    // Mirrors the server's `pickDefaultCapacity`, so the modal pre-selects the
    // same capacity a player would see pre-selected.
    const defaultCapacity = useMemo(
        () => resolveAdminBookingCapacity(sport, offeredCapacities),
        [sport, offeredCapacities],
    );

    // What this control may OFFER — a strict subset of what the server accepts.
    //
    // Configured organization: the configured capacities plus the sport's legacy
    // capacity (D3 rule 3), every one of which the server both seats AND prices
    // at the value picked.
    //
    // Unconfigured organization: the sport's legacy capacity and NOTHING else.
    // Rule 4 also accepts `legacyClientCapacity(sport)`, but that value exists
    // to let a shipped pre-picker MOBILE build be charged the amount it was
    // shown, and on the eight sports where the two differ the server seats the
    // booking at one number and prices it at the other (D30) — so offering it
    // here billed the payer and the joiners of one game at different rates. See
    // `resolveOfferableAdminBookingCapacities` for the worked €40-court example.
    //
    // Fed the RAW option list, not `offeredCapacities`: which rule applies is
    // decided by whether the org has ANY option row for the sport, and the
    // server does not filter by range when deciding that. Passing the
    // range-filtered list would report an org whose only option is out of range
    // as unconfigured, and the modal would then offer 4 for a tennis court the
    // server accepts only 2 on. The helper drops out-of-range values itself.
    const bookableCapacities = useMemo(
        () =>
            resolveOfferableAdminBookingCapacities(
                sport,
                (capacityOptions ?? []).map((option) => option.capacity),
            ),
        [sport, capacityOptions],
    );

    // A pick survives only while the API would still accept it. Keeping a stale
    // one would quote one capacity and have the booking rejected — or worse,
    // create a game with a different number of seats than the price assumed.
    const capacity = useMemo(() => {
        if (pickedCapacity === null || !isBookableCapacity(pickedCapacity)) {
            return defaultCapacity;
        }
        // This used to wave through ANY in-range pick whenever the org had
        // configured nothing, which is the majority of orgs — but rule 4
        // accepts only two values there, so `2` on a padel court came back as a
        // 400 the modal then reported as "no price for 2 players", sending the
        // operator to edit court availabilities for a problem that was not
        // there.
        return bookableCapacities.includes(pickedCapacity)
            ? pickedCapacity
            : defaultCapacity;
    }, [pickedCapacity, defaultCapacity, bookableCapacities]);

    const [playerQuery, setPlayerQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<{
        id: string;
        name: string;
        email: string;
        // MYS-140 — set when the player came from the guest form: a guest has
        // no app account, so the link is shared by the operator (or emailed to
        // `guestEmail`). Undefined for a player picked from the search.
        isGuest?: boolean;
        phoneNumber?: string | null;
    } | null>(null);
    // MYS-140 — "joueur absent de la base". The guest form replaces the search
    // while open; `guestEmail` is what the operator typed there, sent only for
    // a guest and never stored on the player.
    const [showGuestForm, setShowGuestForm] = useState(false);
    const [guestEmail, setGuestEmail] = useState('');
    const [successResult, setSuccessResult] = useState<AdminBookingResponseDto | null>(null);
    // Each booking starts with no player, for the same mounted-between-opens
    // reason as the drag prefill above. `handleClose` already clears these,
    // but a guest request still in flight when the modal closed could land
    // after it; the form drops that late reply, and this is the backstop that
    // keeps the previous guest — and the email typed for them — out of the
    // next booking.
    useEffect(() => {
        if (!isOpen) return;
        setPlayerQuery('');
        setSelectedUser(null);
        setShowGuestForm(false);
        setGuestEmail('');
    }, [isOpen]);
    const { results: playerResults, isLoading: isPlayerLoading } = useEntitySearch(
        organizationId,
        playerQuery,
    );

    // Lower bound for the picker — prevents the operator from submitting a
    // past time and getting a confusing 400 from the API. useMemo so the
    // floor only computes once per modal open rather than on every render.
    // Snapped to the next 15-min boundary so it lines up with the picker's
    // 15-min dropdown grid.
    const minDateTime = useMemo(
        () => roundUpToNextQuarterHour(new Date(Date.now() + 5 * 60 * 1000)),
        [],
    );

    // The "no results" state is non-trivial because useEntitySearch debounces
    // — only show it once the operator has typed enough AND the request has
    // settled. Otherwise the message flickers between keystrokes.
    const showNoResults =
        playerQuery.trim().length >= 2 &&
        !isPlayerLoading &&
        playerResults.length === 0 &&
        !selectedUser;

    // ISO strings for the API. startTime is now a real Date object from the
    // picker — no string parsing needed, just toISOString. Falsy when the
    // operator clears the field, which keeps `canSubmit` honest below.
    const startIso = useMemo(() => {
        if (!startTime) return null;
        try {
            return startTime.toISOString();
        } catch {
            return null;
        }
    }, [startTime]);
    const endIso = useMemo(() => {
        if (!startIso) return null;
        const end = new Date(new Date(startIso).getTime() + durationMinutes * 60_000);
        return end.toISOString();
    }, [startIso, durationMinutes]);

    const pricePreview = useAdminBookingPricePreview({
        organizationId,
        userId: selectedUser?.id ?? null,
        courtId: courtId ?? null,
        startTime: startIso,
        endTime: endIso,
        sportType: sport,
        capacity,
        // gameType drives the application-fee tier on the server (private
        // unranked uses the private fee %, everything else uses the public
        // fee %), so the live preview must follow whatever the operator
        // picks — otherwise the displayed amount drifts from what Stripe
        // actually charges.
        gameType,
        rankedMode: GameRankedMode.UNRANKED,
    });

    const createMutation = useCreateAdminBooking((data) => {
        onCreated?.();
        if (data.checkoutUrl) {
            // Keep the modal open and switch to a success view showing the
            // link — the operator may want to copy it for WhatsApp / SMS.
            setSuccessResult(data);
        } else if (data.gameId) {
            // MYS-140 HARDEN surface 3 — a guest has no app, so a comped
            // booking reaches them only through the email the operator typed;
            // without one, nobody tells the player unless the operator does.
            const recipient = data.recipient;
            let description: string | undefined;
            if (recipient?.isGuest) {
                description = recipient.email
                    ? `Une confirmation va être envoyée à ${recipient.email}.`
                    : "Ce joueur n'a pas l'app et aucun email n'a été renseigné : préviens-le toi-même.";
            }
            toast({
                title: 'Réservation offerte créée',
                description,
                status: 'success',
                duration: description ? 8000 : 4000,
                position: 'top-right',
                isClosable: Boolean(description),
            });
            handleClose();
        }
    });

    const handleClose = () => {
        setPlayerQuery('');
        setSelectedUser(null);
        setShowGuestForm(false);
        setGuestEmail('');
        setSuccessResult(null);
        onClose();
    };

    const handleGuestCreated = (player: AdminGuestPlayerResponseDto, email: string) => {
        const name = [player.firstName, player.lastName].filter(Boolean).join(' ');
        setSelectedUser({
            id: player.id,
            name,
            email: player.email ?? '',
            isGuest: player.isGuest,
            phoneNumber: player.phoneNumber,
        });
        // The typed email only means something for a guest: a registered
        // player is always emailed at their account address, and the API
        // refuses a guestEmail for one.
        setGuestEmail(player.isGuest ? email : '');
        setShowGuestForm(false);
        setPlayerQuery('');
        if (player.alreadyExisted) {
            toast({
                title: player.isGuest
                    ? `${name} était déjà enregistré avec ce numéro — sélectionné.`
                    : `Ce numéro appartient à ${name}, qui a un compte MySportSet.`,
                description: player.isGuest
                    ? undefined
                    : 'Le lien lui sera envoyé dans l’app et par email.',
                status: 'info',
                duration: 6000,
                position: 'top-right',
                isClosable: true,
            });
        }
    };

    const handleCopyLink = async () => {
        if (!successResult?.checkoutUrl) return;
        try {
            await navigator.clipboard.writeText(successResult.checkoutUrl);
            toast({
                title: 'Lien copié',
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        } catch {
            toast({
                title: 'Impossible de copier — sélectionne le lien manuellement',
                status: 'warning',
                duration: 3000,
                position: 'top-right',
            });
        }
    };

    // The server refuses a STRIPE_LINK booking it cannot charge for: zero or
    // negative after discounts, or inside Stripe's 1-49 cent dead zone — which
    // capacity makes reachable, since the PUBLIC share is court/capacity. The
    // preview used to report such an amount as an ordinary price, so the
    // operator filled the whole form and only found out on submit.
    // `isChargeable` is optional on purpose: an API that predates it sends
    // nothing, and `undefined !== false` keeps that reading as chargeable, so
    // this changes nothing against an older server.
    const previewIsUnchargeable = pricePreview.data?.isChargeable === false;

    // Both helper texts below used to name a CAUSE ("no price at this
    // capacity — check the court's pricing") for every failure of the preview
    // read, so a 500, an aborted request or a dropped connection sent the
    // operator to audit pricing rules that were correct. The API says which
    // one it is: `BOOKING_NOT_PRICEABLE` is the unpriced-slot refusal and
    // nothing else is (see `utils/unpriceableBooking.ts`). The submit rules are
    // deliberately NOT narrowed with it — a preview that failed for any reason
    // is still a price the operator has not been shown.
    const previewFailedAsUnpriced = isUnpriceableBookingError(pricePreview.error);

    const canSubmit =
        Boolean(selectedUser) &&
        Boolean(courtId) &&
        Boolean(startIso) &&
        Boolean(endIso) &&
        !createMutation.isLoading &&
        // Wait for the price preview to resolve when sending a payment link
        // — operators should see the amount before charging the player — and
        // don't let them send a link for an amount Stripe will reject.
        //
        // `isError` has to be checked explicitly: an errored react-query has
        // `isLoading === false` and `data === undefined`, so both of the other
        // two guards pass and the button stayed enabled after the quote failed
        // — the one state in which the operator has been shown no amount at
        // all, which is exactly what this condition exists to prevent.
        (paymentMode === AdminBookingPaymentMode.COMPED ||
            (!pricePreview.isLoading &&
                !pricePreview.isError &&
                !previewIsUnchargeable));

    const submit = () => {
        if (!canSubmit || !selectedUser || !courtId || !startIso || !endIso) return;
        const body: CreateAdminBookingDto = {
            organizationId,
            userId: selectedUser.id,
            courtId,
            startTime: startIso,
            endTime: endIso,
            sportType: sport,
            capacity,
            gameType,
            rankedMode: GameRankedMode.UNRANKED,
            paymentMode,
            // Per-game recording opt-in. Applies to both COMPED and STRIPE
            // bookings, so it's sent unconditionally (unlike the STRIPE-only
            // payment deadline). Defaults false; only true when THIS court can
            // record and the operator flipped the toggle.
            //
            // Re-checked against the gate rather than sent raw: the reset
            // effect above already clears a stale true, but that is an effect,
            // so its correctness depends on React having flushed it before this
            // click. Re-reading the gate here makes the invariant hold within a
            // single render pass — the body can never claim a court records
            // when the toggle for it is not even on screen.
            recordingEnabled: showRecordingToggle && recordingEnabled,
            // MYS-140 — only for a guest, and only when the operator typed one.
            ...(selectedUser.isGuest && guestEmail.trim()
                ? { guestEmail: guestEmail.trim() }
                : {}),
            // Only ship the override when the operator picked a STRIPE
            // payment mode (COMPED has no Stripe session). The server
            // ignores this field for COMPED anyway, but pruning it from
            // the body keeps the wire shape honest.
            ...(paymentMode === AdminBookingPaymentMode.STRIPE_LINK
                ? { paymentDeadlineMinutes }
                : {}),
        };
        createMutation.mutate(body);
    };

    if (successResult?.checkoutUrl) {
        // MYS-140 — say how the link actually travels. `recipient` is absent
        // on an API that predates it; that keeps the original sentence, which
        // was true for every player such an API could book.
        const recipient = successResult.recipient;
        //
        // The API answers BEFORE the email and the notification go out — the
        // listeners run after the response (fire-and-forget `emit`), and a
        // suppressed address or a mail outage is only logged server-side. So
        // where `recipient` is known the sentences say what WILL be sent, never
        // that it has arrived (MYS-140 HARDEN surface 3).
        let successDeliveryText =
            'Le joueur a reçu une notification et un email avec ce lien. Tu peux aussi le copier pour le partager en WhatsApp / SMS.';
        if (recipient?.isGuest) {
            successDeliveryText = recipient.email
                ? `Un email avec ce lien va être envoyé à ${recipient.email}. Ce joueur n'a pas l'app : tu peux aussi lui envoyer le lien par WhatsApp ou SMS.`
                : "Ce joueur n'a pas l'app et aucun email n'a été renseigné : envoie-lui ce lien par WhatsApp, SMS ou copie-le.";
        } else if (recipient) {
            successDeliveryText = recipient.email
                ? 'Le joueur va recevoir une notification et un email avec ce lien. Tu peux aussi le copier pour le partager en WhatsApp / SMS.'
                : 'Le joueur va recevoir une notification avec ce lien. Tu peux aussi le copier pour le partager en WhatsApp / SMS.';
        }
        const sharePhone = recipient?.phoneNumber ?? selectedUser?.phoneNumber ?? null;
        const shareMessage = buildPaymentLinkShareMessage({
            firstName: selectedUser?.name?.split(' ')[0],
            checkoutUrl: successResult.checkoutUrl,
            expiresAt: successResult.expiresAt,
        });
        const whatsAppShareUrl = buildWhatsAppShareUrl(sharePhone, shareMessage);
        const smsShareUrl = buildSmsShareUrl(sharePhone, shareMessage);
        return (
            <Modal isOpen={isOpen} onClose={handleClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Lien de paiement envoyé</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={4}>
                            <Text fontSize="sm">{successDeliveryText}</Text>
                            <Box
                                p={3}
                                borderWidth={1}
                                borderRadius="md"
                                bg="gray.50"
                                overflowX="auto"
                            >
                                <Code fontSize="xs" whiteSpace="nowrap">
                                    {successResult.checkoutUrl}
                                </Code>
                            </Box>
                            {successResult.expiresAt && (
                                <Text fontSize="xs" color="gray.500">
                                    Le lien expire le{' '}
                                    {new Date(successResult.expiresAt).toLocaleString('fr-FR')}.
                                    Sans paiement avant cette date, le créneau est libéré
                                    automatiquement.
                                </Text>
                            )}
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={handleClose}>
                            Fermer
                        </Button>
                        {whatsAppShareUrl && (
                            <Button
                                as="a"
                                href={whatsAppShareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outline"
                                mr={3}
                            >
                                WhatsApp
                            </Button>
                        )}
                        {smsShareUrl && (
                            <Button as="a" href={smsShareUrl} variant="outline" mr={3}>
                                SMS
                            </Button>
                        )}
                        <Button colorScheme="blue" onClick={handleCopyLink}>
                            Copier le lien
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="lg"
            // Prevent accidental dismissal during a pending mutation. The
            // mutation can't be cancelled mid-flight (the API may have
            // already created the blockade + Stripe session), so closing
            // the modal would leave the operator unsure whether the booking
            // went through.
            closeOnEsc={!createMutation.isLoading}
            closeOnOverlayClick={!createMutation.isLoading}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Réserver pour un joueur</ModalHeader>
                <ModalCloseButton isDisabled={createMutation.isLoading} />
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Joueur</FormLabel>
                            {selectedUser ? (
                                <HStack
                                    justify="space-between"
                                    p={2}
                                    borderWidth={1}
                                    borderRadius="md"
                                >
                                    <Box>
                                        <HStack spacing={2}>
                                            <Text fontWeight="bold">{selectedUser.name}</Text>
                                            {selectedUser.isGuest && (
                                                <Text
                                                    as="span"
                                                    fontSize="xs"
                                                    px={2}
                                                    py={0.5}
                                                    borderRadius="full"
                                                    bg="orange.50"
                                                    color="orange.700"
                                                >
                                                    Invité
                                                </Text>
                                            )}
                                        </HStack>
                                        {selectedUser.isGuest ? (
                                            <Text fontSize="sm" color="gray.500">
                                                {[
                                                    selectedUser.phoneNumber,
                                                    guestEmail.trim() ||
                                                        'pas d’email — tu partageras le lien',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </Text>
                                        ) : (
                                            <Text fontSize="sm" color="gray.500">
                                                {selectedUser.email}
                                            </Text>
                                        )}
                                    </Box>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            setSelectedUser(null);
                                            setGuestEmail('');
                                        }}
                                    >
                                        Changer
                                    </Button>
                                </HStack>
                            ) : showGuestForm ? (
                                <GuestPlayerForm
                                    organizationId={organizationId}
                                    initialFirstName={splitSearchIntoName(playerQuery).firstName}
                                    initialLastName={splitSearchIntoName(playerQuery).lastName}
                                    onCancel={() => setShowGuestForm(false)}
                                    onCreated={handleGuestCreated}
                                />
                            ) : (
                                <>
                                    <InputGroup>
                                        <InputLeftElement>
                                            {isPlayerLoading ? <Spinner size="sm" /> : null}
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Nom, prénom ou email…"
                                            value={playerQuery}
                                            onChange={(e) => setPlayerQuery(e.target.value)}
                                        />
                                    </InputGroup>
                                    {playerResults.length > 0 && (
                                        <List
                                            mt={2}
                                            borderWidth={1}
                                            borderRadius="md"
                                            maxH="200px"
                                            overflowY="auto"
                                        >
                                            {playerResults.map((user) => (
                                                <ListItem
                                                    key={user.id}
                                                    px={3}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: 'gray.50' }}
                                                    onClick={() => {
                                                        setSelectedUser({
                                                            id: user.id,
                                                            name: user.name,
                                                            email: user.email,
                                                        });
                                                        setPlayerQuery('');
                                                    }}
                                                >
                                                    <Text fontSize="sm" fontWeight="bold">
                                                        {user.name}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        {user.email}
                                                    </Text>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                    {showNoResults && (
                                        <Text mt={2} fontSize="sm" color="gray.500">
                                            Aucun joueur trouvé. Vérifie l'orthographe, ou
                                            ajoute-le comme invité : il n'a pas besoin de
                                            l'app pour recevoir le lien.
                                        </Text>
                                    )}
                                    {/* MYS-140 — always offered, not only on an
                                        empty search: the player the operator
                                        has on the phone may share a name with
                                        someone already in the base. */}
                                    <Button
                                        mt={2}
                                        size="sm"
                                        variant="link"
                                        colorScheme="blue"
                                        onClick={() => setShowGuestForm(true)}
                                    >
                                        Joueur absent de la base ? Ajouter un invité
                                    </Button>
                                </>
                            )}
                        </FormControl>

                        <HStack spacing={4} align="start">
                            <FormControl>
                                <FormLabel>Terrain</FormLabel>
                                <Select
                                    value={courtId}
                                    onChange={(e) => {
                                        const newId = Number(e.target.value);
                                        setCourtId(newId);
                                        const newCourt = courts.find((c) => c.id === newId);
                                        if (newCourt?.sports?.[0]) {
                                            setSport(newCourt.sports[0]);
                                        }
                                    }}
                                >
                                    {courts.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Sport</FormLabel>
                                <Select
                                    value={sport}
                                    onChange={(e) => setSport(e.target.value as SportType)}
                                >
                                    {(
                                        courts.find((c) => c.id === courtId)?.sports ?? [
                                            SportType.PADEL,
                                        ]
                                    ).map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Type de match</FormLabel>
                                <Select
                                    value={gameType}
                                    onChange={(e) =>
                                        setGameType(e.target.value as GameType)
                                    }
                                >
                                    <option value={GameType.PUBLIC}>Public</option>
                                    <option value={GameType.PRIVATE}>Privé</option>
                                </Select>
                            </FormControl>
                        </HStack>

                        <HStack spacing={4} align="start">
                            <FormControl>
                                <FormLabel>Début</FormLabel>
                                {/* react-datepicker (same component used by
                                    CreateEventForm) — gives us a real
                                    15-minute time grid in the dropdown. The
                                    native <input type="datetime-local"> step
                                    attribute is honored by Chrome only for
                                    keyboard validation; the dropdown UI
                                    still showed every minute, which is what
                                    the operator was seeing. */}
                                <DatePicker
                                    selected={startTime}
                                    onChange={(date: Date | null) => setStartTime(date)}
                                    showTimeSelect
                                    timeIntervals={15}
                                    dateFormat="Pp"
                                    minDate={minDateTime}
                                    wrapperClassName="date-picker-wrapper"
                                    className="date-picker-input"
                                    popperProps={{ strategy: 'fixed' }}
                                />
                            </FormControl>
                            <FormControl maxW="160px">
                                <FormLabel>Durée (min)</FormLabel>
                                <Input
                                    type="number"
                                    min={30}
                                    step={30}
                                    value={durationMinutes}
                                    onChange={(e) =>
                                        setDurationMinutes(Number(e.target.value) || 90)
                                    }
                                />
                            </FormControl>
                        </HStack>

                        {/* Joueurs on its own row — pulling it out of the
                            Début/Durée HStack gives the date-picker enough
                            horizontal room to render its full "5/14/2026,
                            4:00 PM" string without truncation. */}
                        {/* Capacity decides BOTH the price (a pricing row can
                            be configured per capacity) and how every later
                            joiner's share is divided, so it must be one of the
                            capacities the organization actually sells. When
                            the org has configured options for this sport we
                            offer exactly those; otherwise we fall back to a
                            free field seeded with the sport's own default —
                            the number the server assumes when a client sends
                            none. It used to be hardcoded to 4 for every sport,
                            which created 4-seat tennis games that then charged
                            each joiner half of what they owed. */}
                        <FormControl maxW="260px">
                            <FormLabel>Joueurs</FormLabel>
                            {offeredCapacities.length > 0 ? (
                                <>
                                    <Select
                                        value={capacity}
                                        onChange={(e) =>
                                            setPickedCapacity(Number(e.target.value))
                                        }
                                    >
                                        {offeredCapacities.map((option) => (
                                            <option
                                                key={option.capacity}
                                                value={option.capacity}
                                            >
                                                {option.capacity} joueurs
                                                {option.isDefault ? ' (par défaut)' : ''}
                                            </option>
                                        ))}
                                        {/* The sport's legacy capacity stays
                                            selectable even when unconfigured —
                                            the server accepts it (D3 rule 3)
                                            and removing it would take away a
                                            booking the operator can make
                                            today. */}
                                        {!offeredCapacities.some(
                                            (option) =>
                                                option.capacity ===
                                                sportTypeToCapacity(sport),
                                        ) && (
                                            <option value={sportTypeToCapacity(sport)}>
                                                {sportTypeToCapacity(sport)} joueurs
                                                (standard {sport})
                                            </option>
                                        )}
                                    </Select>
                                    <FormHelperText>
                                        Le prix et le partage par joueur suivent la
                                        capacité choisie.
                                    </FormHelperText>
                                </>
                            ) : (
                                <>
                                    {/* NOT a free 1–20 field: with no capacity
                                        options configured the server accepts
                                        only the sport's legacy capacity and 4
                                        (D3 rule 4). Offering the whole range
                                        produced a 400 the operator could only
                                        read as a court-pricing problem. */}
                                    <Select
                                        value={capacity}
                                        onChange={(e) =>
                                            setPickedCapacity(Number(e.target.value))
                                        }
                                    >
                                        {bookableCapacities.map((option) => (
                                            <option key={option} value={option}>
                                                {option} joueurs
                                                {option === sportTypeToCapacity(sport)
                                                    ? ` (standard ${sport})`
                                                    : ''}
                                            </option>
                                        ))}
                                    </Select>
                                    {/* The helper text below is ADVICE, and on a
                                        failed read it is false advice: it sends
                                        an operator whose organization already
                                        sells 2 and 4 to the capacity-management
                                        screen to create options that exist,
                                        which is how a duplicate gets made. The
                                        empty list and the sentence are the same
                                        claim — "you have configured nothing" —
                                        so both have to go when we do not know.

                                        The select stays usable rather than being
                                        replaced: the sport's legacy capacity is
                                        the one value the server accepts under
                                        BOTH rule 3 (configured) and rule 4
                                        (unconfigured) and prices at the value
                                        picked, so a booking made here is correct
                                        whichever the org really is. Withholding
                                        it would take away a booking the operator
                                        can legitimately make, to punish a read
                                        that failed somewhere else.

                                        Scoped to this branch deliberately: with
                                        `offeredCapacities.length > 0` the list
                                        on screen came from a real successful
                                        read (React Query keeps it through a
                                        failed background refetch), so there is
                                        no false statement there to correct. */}
                                    {isCapacityOptionsError ? (
                                        <Box mt={2}>
                                            <InlineErrorState
                                                message={translate(
                                                    'game_capacity_options.error_loading',
                                                )}
                                            />
                                        </Box>
                                    ) : (
                                        <FormHelperText>
                                            Configure des capacités de jeu pour ce sport
                                            pour en proposer d'autres.
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        </FormControl>

                        {showRecordingToggle && (
                            <FormControl
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Box pr={4}>
                                    <FormLabel htmlFor="recording-toggle" mb={1}>
                                        Enregistrer ce match
                                    </FormLabel>
                                    <FormHelperText mt={0}>
                                        La vidéo sera capturée automatiquement une fois
                                        le score validé.
                                    </FormHelperText>
                                </Box>
                                <Switch
                                    id="recording-toggle"
                                    colorScheme="brand"
                                    isChecked={recordingEnabled}
                                    onChange={(e) =>
                                        setRecordingEnabled(e.target.checked)
                                    }
                                />
                            </FormControl>
                        )}

                        <FormControl>
                            <FormLabel>Paiement</FormLabel>
                            <RadioGroup
                                value={paymentMode}
                                onChange={(v) =>
                                    setPaymentMode(v as AdminBookingPaymentMode)
                                }
                            >
                                <Stack>
                                    <Radio value={AdminBookingPaymentMode.STRIPE_LINK}>
                                        Envoyer un lien de paiement
                                    </Radio>
                                    <Radio value={AdminBookingPaymentMode.COMPED}>
                                        Offrir (gratuit)
                                    </Radio>
                                </Stack>
                            </RadioGroup>
                            {paymentMode === AdminBookingPaymentMode.STRIPE_LINK && (
                                <FormHelperText
                                    color={
                                        previewIsUnchargeable ||
                                        pricePreview.isError
                                            ? 'red.500'
                                            : undefined
                                    }
                                >
                                    {pricePreview.isLoading
                                        ? 'Calcul du prix…'
                                        : pricePreview.isError
                                            // Don't fall through to the neutral
                                            // "pick a slot" hint — the request
                                            // WAS made and failed. Name the
                                            // pricing cause only when the API
                                            // said so; otherwise say that the
                                            // price could not be fetched, which
                                            // is all we know.
                                            ? previewFailedAsUnpriced
                                                ? `Aucun prix pour ${capacity} joueurs sur ce créneau — vérifie la tarification du terrain.`
                                                : `Le prix n'a pas pu être calculé — réessaie dans un instant.`
                                            : previewIsUnchargeable && pricePreview.data
                                            // Still show the amount — the
                                            // operator needs to see WHY it is
                                            // unbookable (usually a cheap court
                                            // split across a large capacity) —
                                            // and name the way out.
                                            ? `${buildAdminBookingAmountLabel({
                                                  totalAmount: pricePreview.data.totalAmount,
                                                  currency: pricePreview.data.currency,
                                                  payerAmountBasis: pricePreview.data.payerAmountBasis,
                                                  capacity,
                                              })} — inférieur au minimum de paiement Stripe (${(pricePreview.data.minimumChargeAmount ?? 0.5).toFixed(2)} ${pricePreview.data.currency.toUpperCase()}). Utilise « Offrir (gratuit) » pour créer cette réservation.`
                                            : pricePreview.data
                                            // The price preview DTO returns totalAmount in MAJOR
                                            // units (euros), not Stripe minor units (cents). Dividing
                                            // by 100 here would render a €42 booking as "0.42 EUR".
                                            //
                                            // ...and the amount alone does not say whether it is one
                                            // seat or the whole court, which is the difference between
                                            // a correct phone quote and one understated by a factor of
                                            // `capacity`. `payerAmountBasis` is the server's answer.
                                            ? buildAdminBookingAmountLabel({
                                                  totalAmount: pricePreview.data.totalAmount,
                                                  currency: pricePreview.data.currency,
                                                  payerAmountBasis: pricePreview.data.payerAmountBasis,
                                                  capacity,
                                              })
                                            : 'Sélectionne un joueur, un terrain et un créneau pour voir le prix.'}
                                </FormHelperText>
                            )}
                            {/* COMPED used to render no price signal at all, on
                                the reasoning that a free booking has no price to
                                show. It has one: the capacity stamped on the
                                comped game is the row selector every later
                                JOINER is charged by, so a slot with no price at
                                this capacity produces a game nobody can ever
                                join and whose replacement links are unredeemable
                                from birth. The server refuses that shape now;
                                this tells the operator before they submit.
                                Deliberately NOT wired into `canSubmit`: an
                                unpriced slot (no rows for the sport at all) is a
                                legitimate comp-only court, which the server
                                still allows. */}
                            {paymentMode === AdminBookingPaymentMode.COMPED && (
                                <FormHelperText
                                    color={
                                        pricePreview.isError
                                            ? 'red.500'
                                            : undefined
                                    }
                                >
                                    {pricePreview.isError
                                        ? previewFailedAsUnpriced
                                            ? `Aucun prix pour ${capacity} joueurs sur ce créneau : la réservation sera offerte, mais personne ne pourra rejoindre cette partie. Vérifie la tarification du terrain.`
                                            : `Le prix n'a pas pu être vérifié : la réservation sera offerte, mais impossible de confirmer que d'autres joueurs pourront la rejoindre.`
                                        : 'Cette réservation ne sera pas facturée.'}
                                </FormHelperText>
                            )}
                        </FormControl>

                        {/* Per-booking expiry override. Only visible in
                            STRIPE_LINK mode (COMPED has no Stripe session
                            and no expiry to manage). Initial value is the
                            org's bookingPaymentDeadlineMinutes; the
                            operator can pick a longer or shorter window
                            for THIS booking only. The org default value
                            is highlighted as the "Par défaut" entry so
                            the operator knows what the baseline is. */}
                        {paymentMode === AdminBookingPaymentMode.STRIPE_LINK && (
                            <FormControl maxW="280px">
                                <FormLabel>Lien valide pendant</FormLabel>
                                <Select
                                    value={paymentDeadlineMinutes}
                                    onChange={(e) =>
                                        setPaymentDeadlineMinutes(
                                            Number(e.target.value),
                                        )
                                    }
                                >
                                    {DEADLINE_PRESETS
                                        .filter(
                                            // Inject the org default if it's
                                            // not already in the preset list,
                                            // so the operator's baseline is
                                            // always reachable in one click.
                                            (m, _, arr) =>
                                                m !== orgDefaultDeadline ||
                                                arr.includes(orgDefaultDeadline),
                                        )
                                        .concat(
                                            DEADLINE_PRESETS.includes(
                                                orgDefaultDeadline,
                                            )
                                                ? []
                                                : [orgDefaultDeadline],
                                        )
                                        .sort((a, b) => a - b)
                                        .map((minutes) => (
                                            <option key={minutes} value={minutes}>
                                                {formatDeadlineLabel(
                                                    minutes,
                                                    minutes === orgDefaultDeadline,
                                                )}
                                            </option>
                                        ))}
                                </Select>
                                <FormHelperText>
                                    Le joueur a ce délai pour payer avant que le
                                    créneau ne soit libéré automatiquement.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={handleClose}>
                        Annuler
                    </Button>
                    <Button
                        colorScheme="blue"
                        isDisabled={!canSubmit}
                        isLoading={createMutation.isLoading}
                        onClick={submit}
                    >
                        {paymentMode === AdminBookingPaymentMode.STRIPE_LINK
                            ? 'Envoyer le lien'
                            : 'Créer la réservation'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
